import { useFrame } from '@react-three/fiber'
import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'

interface PropsParticulas {
  ativo: boolean
  tipo: 'poLaminado' | 'estilhacosTemperado'
  origem: [number, number, number]
  duracaoSegundos?: number
  onFinalizar?: () => void
}

/**
 * Sistema de Partículas 3D para Simulação de Resíduos de Vidro:
 *
 * - `poLaminado`: Nuvem de micropartículas/pó de sílica emanando da serra corta-vidro.
 * - `estilhacosTemperado`: Dispersão radial de fragmentos vítreos cúbicos da punção.
 */
export function SistemaParticulasVidro({
  ativo,
  tipo,
  origem,
  duracaoSegundos = 2.5,
  onFinalizar,
}: PropsParticulas) {
  const pointsRef = useRef<THREE.Points>(null)
  const tempoDecorrido = useRef(0)

  const numParticulas = tipo === 'poLaminado' ? 450 : 250

  const { posicoes, velocidades, tamanhos } = useMemo(() => {
    const pos = new Float32Array(numParticulas * 3)
    const vel = new Float32Array(numParticulas * 3)
    const sz = new Float32Array(numParticulas)

    for (let i = 0; i < numParticulas; i++) {
      pos[i * 3] = origem[0] + (Math.random() - 0.5) * 0.05
      pos[i * 3 + 1] = origem[1] + (Math.random() - 0.5) * 0.05
      pos[i * 3 + 2] = origem[2] + (Math.random() - 0.5) * 0.05

      if (tipo === 'poLaminado') {
        // Poeira leve flutuando e se dispersando no habitáculo
        vel[i * 3] = (Math.random() - 0.5) * 0.4
        vel[i * 3 + 1] = (Math.random() - 0.2) * 0.3
        vel[i * 3 + 2] = -0.25 - Math.random() * 0.45 // vai em direção ao interior
        sz[i] = 0.015 + Math.random() * 0.02
      } else {
        // Estilhaços projetados prioritariamente para o INTERIOR do veículo (em direção à vítima)
        const dirInteriorX = origem[0] < -0.1 ? 0.7 + Math.random() * 0.8 : origem[0] > 0.1 ? -(0.7 + Math.random() * 0.8) : (Math.random() - 0.5) * 0.5
        vel[i * 3] = dirInteriorX + (Math.random() - 0.5) * 0.4
        vel[i * 3 + 1] = 0.2 + Math.random() * 0.8
        vel[i * 3 + 2] = (Math.random() - 0.5) * 0.8 - (origem[2] > 0.5 ? 0.4 : 0)
        sz[i] = 0.022 + Math.random() * 0.032
      }
    }

    return { posicoes: pos, velocidades: vel, tamanhos: sz }
  }, [numParticulas, origem, tipo])

  useEffect(() => {
    if (ativo) {
      tempoDecorrido.current = 0
    }
  }, [ativo])

  useFrame((_, dt) => {
    if (!ativo || !pointsRef.current) return

    tempoDecorrido.current += dt
    const posAttr = pointsRef.current.geometry.attributes.position as THREE.BufferAttribute
    const posArray = posAttr.array as Float32Array

    for (let i = 0; i < numParticulas; i++) {
      if (tipo === 'estilhacosTemperado') {
        velocidades[i * 3 + 1] -= 9.8 * dt // gravidade
      } else {
        velocidades[i * 3 + 1] -= 0.5 * dt // poeira leve assentando
      }

      posArray[i * 3] += velocidades[i * 3] * dt
      posArray[i * 3 + 1] += velocidades[i * 3 + 1] * dt
      posArray[i * 3 + 2] += velocidades[i * 3 + 2] * dt
    }

    posAttr.needsUpdate = true

    if (tempoDecorrido.current >= duracaoSegundos) {
      onFinalizar?.()
    }
  })

  if (!ativo) return null

  const corParticula = tipo === 'poLaminado' ? '#e2e8f0' : '#bae6fd'
  const opacidade = tipo === 'poLaminado' ? 0.75 : 0.85

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          args={[posicoes, 3]}
        />
        <bufferAttribute
          attach="attributes-size"
          args={[tamanhos, 1]}
        />
      </bufferGeometry>
      <pointsMaterial
        size={tipo === 'poLaminado' ? 0.025 : 0.04}
        color={corParticula}
        transparent
        opacity={opacidade}
        depthWrite={false}
      />
    </points>
  )
}

